import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  Validators,
  ValidatorFn,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Modal } from '../../interfaces/modal.interface';
import { InputModalField } from '@app/core/interfaces/input-modal-field.interface';
import { NgTemplateOutlet } from '@angular/common';
import {
  NgSelectComponent,
  NgOptionTemplateDirective,
  NgLabelTemplateDirective,
} from '@ng-select/ng-select';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { Button, ButtonSeverity } from 'primeng/button';
import { Select } from 'primeng/select';
import { Checkbox } from 'primeng/checkbox';
import { Textarea } from 'primeng/textarea';
import { InputText } from 'primeng/inputtext';
import { Ag1BannerComponent } from '@app/core/components/ag1-banner/ag1-banner.component';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { FileUpload, FileSelectEvent } from 'primeng/fileupload';

export interface InputModalConfig {
  title: string;
  fields?: Array<InputModalField>;
  field?: InputModalField;

  confirmBtnText?: string;
  confirmBtnClass?: string;
  onConfirm: (
    result:
      | Array<string>
      | Array<number>
      | Array<boolean>
      | string
      | number
      | boolean,
  ) => void;
  onCancel?: () => void;
  markAsDirty?: boolean;
}

@Component({
  selector: 'ag-input-modal',
  templateUrl: './input-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [HumanizePipe],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    NgTemplateOutlet,
    NgLabelTemplateDirective,
    Button,
    Select,
    Checkbox,
    Textarea,
    InputText,
    Ag1BannerComponent,
    Dialog,
    Message,
    FileUpload,
  ],
})
export class InputModalComponent implements OnInit, Modal {
  private formBuilder = inject(UntypedFormBuilder);
  private humanize = inject(HumanizePipe);

  form: UntypedFormGroup;
  get formFields(): UntypedFormArray {
    return this.form.get('formFields') as UntypedFormArray;
  }

  title: string;
  fields: Array<InputModalField>;
  singleField: boolean = false;

  confirmBtnText: string;
  confirmBtnClass: string;
  customErrors: Map<string, string[]> = new Map();

  // Callers across the app still pass Bulma modifier names (eg. 'is-link')
  // via confirmBtnClass, since that prop is shared with ConfirmModalComponent
  // and used by ~40 call sites. Map the ones we support to a p-button
  // severity here so this component doesn't depend on Bulma, without
  // touching those callers.
  /* eslint-disable @typescript-eslint/naming-convention -- keys mirror the Bulma modifier strings callers pass in */
  private static readonly bulmaSeverityMap: Record<string, ButtonSeverity> = {
    'is-link': 'primary',
    'is-primary': 'primary',
    'is-info': 'info',
    'is-danger': 'danger',
    'is-success': 'success',
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  get confirmButtonSeverity(): ButtonSeverity {
    return (
      InputModalComponent.bulmaSeverityMap[this.confirmBtnClass] ?? 'primary'
    );
  }

  onConfirm: (
    result:
      | Array<string>
      | Array<number>
      | Array<boolean>
      | string
      | number
      | boolean,
  ) => void;
  onCancel: () => void;

  isLoading: boolean = false;
  isVisible: boolean = false;
  formSubmitted: boolean = false;
  formDirty: boolean = false;
  disableHumanize: boolean = false;
  onAction: Promise<boolean>;

  private onActionResolve: (action: boolean) => void;

  constructor() {
    this.onAction = new Promise((resolve) => (this.onActionResolve = resolve));
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      formFields: new UntypedFormArray(this.fieldsWithValidators()),
    });

    this.fields.forEach((field) => {
      this.customErrors[field.inputLabel] = [];
    });
  }

  fieldsWithValidators(): Array<UntypedFormControl> {
    return this.fields.map((field) => {
      const validators: Array<ValidatorFn> = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      switch (field.inputType) {
        case 'email':
          this.addEmailValidation(validators);
          break;
        case 'float':
          this.addFloatValidation(field, validators);
          break;
        case 'text':
          this.addTextValidation(field, validators);
          break;
        case 'boolean':
          break;
        case 'dropdown':
          break;
        case 'textarea':
          break;
        case 'file':
          break;
        case 'ng-select':
          break;
        case 'infoMessage':
          break;
        case 'bannerMessage':
          break;
        default:
          throw new Error(`Unrecognized input type ${field.inputType}`);
      }
      return new UntypedFormControl(field.defaultValue, validators);
    });
  }

  getDropdownOptions(
    field: InputModalField,
  ): Array<{ label: string; value: string }> {
    return (field.dropDownOptions || []).map((option) => ({
      label: field.disableHumanize
        ? option
        : (this.humanize.transform(option) as string),
      value: option,
    }));
  }

  restrictInvalidCharacters(event: KeyboardEvent): boolean {
    const invalidChars = ['e', 'E', '+', 'ArrowUp', 'ArrowDown'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  addTextValidation(field: InputModalField, validators: Array<ValidatorFn>) {
    if (field.minLength !== undefined) {
      validators.push(Validators.minLength(field.minLength));
    }

    if (field.maxLength !== undefined) {
      validators.push(Validators.maxLength(field.maxLength));
    }
  }

  addEmailValidation(validators: Array<ValidatorFn>) {
    validators.push(Validators.email);
  }

  addFloatValidation(field: InputModalField, validators: Array<ValidatorFn>) {
    if (field.min !== undefined) {
      validators.push(Validators.min(field.min));
    }
    if (field.max !== undefined) {
      validators.push(Validators.max(field.max));
    }
  }

  configure(config: InputModalConfig) {
    this.title = config.title;
    this.fields = config.fields;

    if (config.field) {
      this.singleField = true;
      this.fields = [config.field];
    }

    if (config.markAsDirty) {
      this.formDirty = true;
    }

    this.confirmBtnClass = config.confirmBtnClass || 'is-link';
    this.confirmBtnText = config.confirmBtnText || 'Ok';
    this.onConfirm = config.onConfirm;
    this.onCancel = config.onCancel || this.hide;
  }

  show() {
    this.isVisible = true;
    if (this.formDirty) {
      this.form?.markAsDirty();
    }
  }

  hide() {
    this.isVisible = false;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  onConfirmClick() {
    this.formSubmitted = true;

    if (this.form.invalid) {
      return;
    }

    this.onActionResolve(true);

    let result = this.formFields.value;

    // if a single field was passed instead of fields,
    // unpack the result for ease of use
    if (this.singleField) {
      result = result[0];
    }

    this.onConfirm(result);
  }

  onCancelClick() {
    // Can't cancel after submission
    if (this.isLoading) {
      return;
    }

    this.onActionResolve(false);
    this.onCancel();
  }

  addCustomError(inputLabel: string, message: string) {
    if (this.customErrors[inputLabel].indexOf(message) === -1) {
      this.customErrors[inputLabel].push(message);
    }
  }

  get formControls() {
    return this.formFields.controls;
  }

  handleFileSelection(event: FileSelectEvent, index: number): void {
    const control = this.formFields.at(index);
    // PrimeNG clears the native file input right after emitting onSelect.
    // Since event.files is a live reference to that input's FileList rather
    // than a copy, we snapshot it into a plain array first. Otherwise the
    // control's value reads as empty once PrimeNG clears the input.
    control.setValue(Array.from(event.files));
    // FileUpload has no ControlValueAccessor, so dirty/touched must be set manually.
    control.markAsDirty();
    control.markAsTouched();
  }
}
